import { MessageType } from '@/types/common';
import { createAppSlice } from '../createAppSlice';
import { Message } from '@/enums/message.enum';
import { MESSAGE } from '@/constants/message.constant';
import { transformerArray } from '../transformer';

type ChatboxSliceState = {
  loading: boolean;
  message?: MessageType;
  FileUpload: FileUpload;
  ChatBotFileKeyDetail?: FileKeyConfig;
  historyUploadFileKey?: HistoryUploadKeyFile[];
  paginationHistory: Pagination;
  paginationHistoryChatBox: Pagination;
  HistoryChatBox?: ChatTurn[];
};

const initialState: ChatboxSliceState = {
  loading: false,
  FileUpload: {} as Record<
    TypeUpload,
    {
      url: string;
      name: string;
    }
  >,
  ChatBotFileKeyDetail: undefined,
  paginationHistory: { curPage: 1, limitPage: 10, totalRows: 0, totalPage: 0 },
  paginationHistoryChatBox: {
    curPage: 1,
    limitPage: 10,
    totalRows: 0,
    totalPage: 0
  },
  HistoryChatBox: []
};

export const chatboxSlice = createAppSlice({
  name: 'chatbox',
  initialState,
  reducers: (create) => ({
    uploadFileSuccess: create.reducer(
      (
        state,
        action: { payload: { type: TypeUpload; url: string; name: string } }
      ) => {
        state.FileUpload[action.payload.type] = {
          url: action.payload.url,
          name: action.payload.name
        };
      }
    ),
    removeFile: create.reducer(
      (state, action: { payload: { type: TypeUpload } }) => {
        delete state.FileUpload[action.payload.type];
      }
    ),
    clearAllFiles: create.reducer((state) => {
      state.FileUpload = {} as Record<
        TypeUpload,
        { url: string; name: string }
      >;
    }),
    getFileKeyConfig: create.asyncThunk(
      async () => {
        const response = await apiFetcher.get('chatbot/get-file-key-detail');

        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
          state.message = undefined;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.ChatBotFileKeyDetail = action.payload;
        },
        rejected: (state, action) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: action.error?.code ?? 'Lấy cấu hình file thất bại!',
            ...(action.error?.message && { description: action.error?.message })
          };
          state.ChatBotFileKeyDetail = undefined;
        }
      }
    ),
    uploadAllFileKeyConfig: create.asyncThunk(
      async (data: uploadAllFileKeyConfig) => {
        const response = await apiFetcher.post(
          'chatbot/upload-file-data',
          data
        );

        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
          state.message = undefined;
        },
        fulfilled: (state) => {
          state.loading = false;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.UPLOAD_ALL_FILE_KEY_CONFIG_SUCCESS
          };
        },
        rejected: (state) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: MESSAGE.UPLOAD_ALL_FILE_KEY_CONFIG_FAIL
          };
        }
      }
    ),
    getUploadHistory: create.asyncThunk(
      async ({
        offset = 1,
        limit = 10
      }: {
        offset?: number;
        limit?: number;
      }) => {
        const response = await apiFetcher.get('chatbot/upload-history', {
          params: {
            page: offset,
            limit
          }
        });

        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
          state.message = undefined;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.historyUploadFileKey = transformerArray(
            action.payload.listData
          );
          state.paginationHistory = action.payload.paging;
        },
        rejected: (state, action) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: action.error?.code ?? 'Lấy lịch sử tải lên thất bại!',
            ...(action.error?.message && { description: action.error?.message })
          };
        }
      }
    ),
    getChatBoxHistory: create.asyncThunk(
      async ({
        offset = 1,
        limit = 5,
        startDate,
        endDate,
        search
      }: {
        offset?: number;
        limit?: number;
        startDate?: string;
        endDate?: string;
        search?: string;
      }) => {
        const queryParams: Record<string, string | number> = {
          page: offset,
          limit
        };

        if (startDate) queryParams.startDate = startDate;
        if (endDate) queryParams.endDate = endDate;
        if (search && search.trim()) queryParams.search = search.trim();

        const response = await apiFetcher.get('chatbot/get-chat-history', {
          query: queryParams
        });

        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
          state.message = undefined;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.paginationHistoryChatBox = action.payload.paging;

          const isNew = action.payload.paging.curPage === 1;

          if (isNew) {
            state.HistoryChatBox = action.payload.listData || [];
            return;
          }

          const currentList = state.HistoryChatBox || [];
          const newData = action.payload.listData || [];

          const data = new Set(
            [...currentList, ...newData].map((item) => JSON.stringify(item))
          );
          state.HistoryChatBox = Array.from(data).map((item) =>
            JSON.parse(item)
          );
        },
        rejected: (state, action) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message:
              action.error?.code ?? 'Lấy lịch sử chat trong chatbox thất bại!',
            ...(action.error?.message && { description: action.error?.message })
          };
        }
      }
    ),
    resetMessages: create.reducer((state) => {
      state.message = undefined;
    }),
    downloadTemplateChatbot: create.asyncThunk(
      async (type: TypeUpload) => {
        const response = await apiFetcher.get('chatbot/download-template', {
          query: { type }
        });
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state) => {
          state.loading = false;
        },
        rejected: (state, action) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: action.error?.code ?? 'Tải template thất bại!',
            ...(action.error?.message && { description: action.error?.message })
          };
        }
      }
    )
  }),
  selectors: {
    watchFileUploads: (state) => state.FileUpload,
    watchFileKeyConfig: (state) => state.ChatBotFileKeyDetail,
    watchChatBoxMessage: (state) => state.message,
    watchHistoryUploadFileKey: (state) => state.historyUploadFileKey,
    watchUploadFileKeyPaginationHistory: (state) => state.paginationHistory,
    watchHistoryChatBox: (state) => state.HistoryChatBox,
    watchPaginationHistoryChatBox: (state) => state.paginationHistoryChatBox
  }
});

export const {
  watchFileUploads,
  watchFileKeyConfig,
  watchChatBoxMessage,
  watchHistoryUploadFileKey,
  watchUploadFileKeyPaginationHistory,
  watchHistoryChatBox,
  watchPaginationHistoryChatBox
} = chatboxSlice.selectors;

export const {
  uploadFileSuccess,
  removeFile,
  clearAllFiles,
  getFileKeyConfig,
  resetMessages,
  uploadAllFileKeyConfig,
  getUploadHistory,
  getChatBoxHistory,
  downloadTemplateChatbot
} = chatboxSlice.actions;
